import React, { useState } from 'react';
import { AlertTriangle, Ban, CheckCircle2, ChevronDown, ChevronRight, ExternalLink, MinusCircle, RotateCcw, UserX } from 'lucide-react';

export const RENAME_API = `${process.env.REACT_APP_BACKEND_URL}/api/ticket-rename`;
export const fmtDT = (s) => (s ? new Date(s).toLocaleString() : '');
export const LIVE = ['scanning', 'applying', 'reverting'];

export const STATUS = {
  ok: { label: 'OK', cls: 'bg-emerald-50 text-emerald-700 border-emerald-200', Icon: CheckCircle2 },
  unsure: { label: 'Needs review', cls: 'bg-amber-50 text-amber-700 border-amber-200', Icon: AlertTriangle },
  same: { label: 'Already standard', cls: 'bg-slate-200 text-slate-700 border-slate-500/40', Icon: MinusCircle },
  skip: { label: 'Skipped', cls: 'bg-slate-100 text-slate-500 border-slate-300', Icon: Ban },
};

export function StatusPill({ status }) {
  const s = STATUS[status] || STATUS.skip;
  return <span className={`inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wide px-1.5 py-0.5 rounded border ${s.cls}`}><s.Icon className="w-3 h-3" />{s.label}</span>;
}

export function SummaryChips({ summary = {}, filter, onFilter }) {
  const total = Object.values(summary).reduce((a, b) => a + b, 0);
  const chips = [['all', 'All', total], ...Object.keys(STATUS).map((k) => [k, STATUS[k].label, summary[k] || 0])];
  return (
    <div className="flex flex-wrap gap-1.5" data-testid="rename-chips">
      {chips.map(([k, label, n]) => (
        <button key={k} type="button" onClick={() => onFilter(k)} data-testid={`rename-chip-${k}`}
          className={`text-xs px-2.5 py-1 rounded-full border transition-colors ${filter === k ? 'bg-white text-slate-900 border-white' : `${(STATUS[k] || {}).cls || 'text-slate-800 border-slate-300'} hover:border-white/70`}`}>
          {label} <span className="font-mono font-bold">{n}</span>
        </button>
      ))}
    </div>
  );
}

export function Progress({ value = 0, total = 0, label }) {
  const pct = total ? Math.round((value / total) * 100) : 0;
  return (
    <div className="space-y-1.5" data-testid="rename-progress">
      <div className="flex justify-between text-xs text-slate-700"><span>{label}</span><span className="font-mono">{total ? `${value}/${total}` : ''}</span></div>
      <div className="h-2 rounded-full bg-white overflow-hidden"><div className="h-full bg-emerald-500 transition-[width] duration-500" style={{ width: `${pct}%` }} /></div>
    </div>
  );
}

export function RenameRow({ row, state, onToggle, onEdit }) {
  const editable = !!row.proposed && row.status !== 'same';
  const dim = row.status === 'same' || row.status === 'skip';
  return (
    <div className={`grid grid-cols-[24px_minmax(0,1fr)] sm:grid-cols-[24px_minmax(0,1fr)_minmax(0,1fr)] gap-x-3 gap-y-1 items-start px-3 py-2 border-b border-slate-200 ${dim ? 'opacity-50' : ''} ${state?.checked ? 'bg-emerald-50' : ''}`}
      data-testid={`rename-row-${row.file_id}`}>
      <input type="checkbox" className="mt-1 h-4 w-4 accent-emerald-500 disabled:opacity-30" disabled={!editable} checked={!!state?.checked}
        onChange={() => onToggle(row.file_id)} data-testid={`rename-check-${row.file_id}`} aria-label={`Rename ${row.current}`} />
      <div className="min-w-0">
        <div className="flex items-center gap-1.5 min-w-0">
          <StatusPill status={row.status} />
          <span className="text-sm text-slate-800 truncate" title={row.current} data-testid={`rename-current-${row.file_id}`}>{row.current}</span>
          {row.link && <a href={row.link} target="_blank" rel="noreferrer" className="text-slate-500 hover:text-slate-900 shrink-0" title="Open in Drive"><ExternalLink className="w-3.5 h-3.5" /></a>}
        </div>
        {row.expiry && <p className="text-[11px] text-slate-500">expiry {row.expiry}{row.expiry_source ? ` · from ${row.expiry_source}` : ''}</p>}
      </div>
      <div className="min-w-0 col-start-2 sm:col-start-3">
        {editable ? (
          <input value={state?.name ?? row.proposed} onChange={(e) => onEdit(row.file_id, e.target.value)} data-testid={`rename-input-${row.file_id}`}
            className={`w-full bg-white border rounded-md px-2 py-1 text-sm text-slate-900 font-mono focus:outline-none focus:border-emerald-400 ${row.status === 'unsure' ? 'border-amber-300' : 'border-slate-300'}`} />
        ) : (
          <span className="text-sm text-slate-500 font-mono truncate block" title={row.proposed || ''}>{row.proposed || '—'}</span>
        )}
        {row.reason && <p className={`text-[11px] mt-0.5 ${row.status === 'unsure' ? 'text-amber-700' : 'text-slate-500'}`} data-testid={`rename-reason-${row.file_id}`}>{row.reason}</p>}
      </div>
    </div>
  );
}

export function PersonGroup({ group, sel, onToggle, onEdit, onGroupAll }) {
  const [open, setOpen] = useState(true);
  const tickable = group.rows.filter((r) => r.proposed && r.status !== 'same');
  const ticked = tickable.filter((r) => sel[r.file_id]?.checked).length;
  const slug = group.folder.replace(/[^a-z0-9]+/gi, '-').toLowerCase();
  return (
    <div className="border border-slate-200 rounded-xl overflow-hidden" data-testid={`rename-group-${slug}`}>
      <div className="flex items-center gap-2 px-3 py-2 bg-white">
        <button type="button" onClick={() => setOpen(!open)} className="text-slate-700 hover:text-slate-900" data-testid={`rename-group-toggle-${slug}`}>
          {open ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
        </button>
        <div className="min-w-0 flex-1">
          <span className="text-slate-900 font-semibold text-sm">{group.name}</span>
          {group.folder !== group.name && <span className="text-xs text-slate-500 ml-2">folder “{group.folder}”</span>}
          {!group.user_id && <span className="inline-flex items-center gap-1 ml-2 text-[10px] text-rose-700 bg-rose-50 border border-rose-200 rounded px-1.5 py-0.5"><UserX className="w-3 h-3" /> no account</span>}
        </div>
        <span className="text-xs text-slate-500 font-mono" data-testid={`rename-group-count-${slug}`}>{ticked}/{tickable.length} ticked</span>
        {tickable.length > 0 && (
          <div className="flex gap-1">
            <button type="button" onClick={() => onGroupAll(group, true)} className="text-[11px] text-emerald-700 hover:underline" data-testid={`rename-group-all-${slug}`}>all</button>
            <span className="text-slate-600 text-[11px]">·</span>
            <button type="button" onClick={() => onGroupAll(group, false)} className="text-[11px] text-slate-700 hover:underline" data-testid={`rename-group-none-${slug}`}>none</button>
          </div>
        )}
      </div>
      {open && group.rows.map((r) => <RenameRow key={r.file_id} row={r} state={sel[r.file_id]} onToggle={onToggle} onEdit={onEdit} />)}
    </div>
  );
}

export function RunsList({ jobs, onOpen, onUndo, busy }) {
  if (!jobs?.length) return <p className="text-xs text-slate-500">No previous runs.</p>;
  return (
    <div className="space-y-1.5" data-testid="rename-runs">
      {jobs.map((j) => (
        <div key={j.id} className="flex flex-wrap items-center gap-2 text-xs bg-white border border-slate-200 rounded-lg px-3 py-2" data-testid={`rename-run-${j.id}`}>
          <span className="font-mono text-slate-500">{fmtDT(j.created_at)}</span>
          <span className={`px-1.5 py-0.5 rounded font-bold uppercase text-[10px] ${j.status === 'done' ? 'bg-emerald-50 text-emerald-700' : j.status === 'reverted' ? 'bg-slate-200 text-slate-800' : j.status === 'error' ? 'bg-rose-50 text-rose-700' : 'bg-amber-50 text-amber-800'}`}>{j.status}</span>
          <span className="text-slate-700">
            {j.status === 'done' && `${(j.applied || 0) - (j.failed || 0)} renamed${j.failed ? `, ${j.failed} failed` : ''}${j.applied_by ? ` · by ${j.applied_by}` : ''}`}
            {j.status === 'reverted' && `${j.reverted || 0} restored`}
            {j.status === 'ready' && `${j.summary?.ok || 0} OK · ${j.summary?.unsure || 0} to review`}
            {j.status === 'error' && j.error}
            {j.status === 'superseded' && 'preview replaced by a newer scan'}
          </span>
          <span className="flex-1" />
          {LIVE.concat('ready').includes(j.status) && <button type="button" onClick={() => onOpen(j.id)} className="text-emerald-700 hover:underline" data-testid={`rename-run-open-${j.id}`}>Open</button>}
          {j.status === 'done' && (
            <button type="button" disabled={busy} onClick={() => onUndo(j.id)} className="inline-flex items-center gap-1 text-amber-700 hover:underline disabled:opacity-50" data-testid={`rename-run-undo-${j.id}`}>
              <RotateCcw className="w-3 h-3" /> Undo
            </button>
          )}
        </div>
      ))}
    </div>
  );
}

export function ResultView({ job, onUndo, undoing }) {
  const [showOk, setShowOk] = useState(false);
  const log = job.log || [];
  const okRows = log.filter((e) => e.ok);
  const failed = log.filter((e) => !e.ok);
  return (
    <div className="space-y-4" data-testid="rename-result">
      <div className="grid sm:grid-cols-2 gap-3">
        <div className="rounded-xl border border-emerald-200 bg-emerald-50 p-4">
          <p className="text-3xl font-black text-emerald-700 font-mono" data-testid="rename-result-ok">{okRows.length}</p>
          <p className="text-sm text-emerald-800">files renamed in Drive{okRows.some((e) => e.record_id) ? ' · matching ticket records updated' : ''}</p>
        </div>
        <div className={`rounded-xl border p-4 ${failed.length ? 'border-rose-200 bg-rose-50' : 'border-slate-200 bg-white'}`}>
          <p className={`text-3xl font-black font-mono ${failed.length ? 'text-rose-700' : 'text-slate-500'}`} data-testid="rename-result-failed">{failed.length}</p>
          <p className="text-sm text-slate-800">failed</p>
        </div>
      </div>
      {failed.length > 0 && (
        <div className="space-y-1" data-testid="rename-result-failures">
          {failed.map((e) => <p key={e.file_id} className="text-xs text-rose-800"><span className="font-mono">{e.old}</span> — {e.error}</p>)}
        </div>
      )}
      <button type="button" onClick={() => setShowOk(!showOk)} className="text-xs text-slate-500 hover:text-slate-900" data-testid="rename-result-toggle">{showOk ? 'Hide' : 'Show'} renamed files</button>
      {showOk && (
        <div className="max-h-64 overflow-y-auto border border-slate-200 rounded-lg divide-y divide-slate-200">
          {okRows.map((e) => <p key={e.file_id} className="text-xs px-3 py-1.5 font-mono"><span className="text-slate-500">{e.old}</span> <span className="text-slate-600">→</span> <span className="text-slate-800">{e.new}</span></p>)}
        </div>
      )}
      {job.status === 'done' && okRows.length > 0 && (
        <button type="button" disabled={undoing} onClick={() => onUndo(job.id)} data-testid="rename-undo"
          className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-amber-50 border border-amber-300 text-amber-800 hover:bg-amber-50 disabled:opacity-50 text-sm font-semibold">
          <RotateCcw className="w-4 h-4" /> Undo this run — put the old names back
        </button>
      )}
    </div>
  );
}
